import { normalizeFormSchema } from '@wix/form-schema';
import { listForms } from '@wix/ambassador-forms-v4-form/http';
import { Fieldset, ListFormsOrder } from '@wix/ambassador-forms-v4-form/types';
import { getRestrictions } from '@wix/ambassador-forms-v4-restrictions/http';
import { createSubmission } from '@wix/ambassador-forms-v4-submission/http';
import { FORMS_PAGE_SIZE, MAX_LIST_FORMS_PAGES, WIX_FORMS_NAMESPACE } from './consts';
import { getRenderedFieldTypes } from './field-support';
/** Fetches the given forms and normalises them the way Wix Forms does before rendering. */
export const fetchForms = async (httpClient, formIds) => {
    if (formIds.length === 0) {
        return {};
    }
    const { data } = await httpClient.request(listForms({ formIds, namespace: WIX_FORMS_NAMESPACE }));
    const byId = {};
    (data?.forms ?? []).forEach((form) => {
        if (form.id) {
            byId[form.id] = normalizeFormSchema(form);
        }
    });
    return byId;
};
/**
 * Lists the site's forms (newest updated first), following the cursor for at most
 * `MAX_LIST_FORMS_PAGES` pages.
 */
export const createListForms = (httpClient) => async () => {
    const summaries = [];
    let cursor;
    for (let page = 0; page < MAX_LIST_FORMS_PAGES; page++) {
        const { data } = await httpClient.request(listForms({
            namespace: WIX_FORMS_NAMESPACE,
            // METADATA: ids, names and dates only; the picker never needs field schemas.
            fieldsets: [Fieldset.METADATA],
            order: ListFormsOrder.UPDATED_DATE_DESC,
            paging: { limit: FORMS_PAGE_SIZE, cursor },
        }));
        (data?.forms ?? []).forEach((form) => {
            if (form.id) {
                summaries.push({
                    id: form.id,
                    name: form.properties?.name ?? '',
                    createdDate: form.createdDate,
                    updatedDate: form.updatedDate,
                });
            }
        });
        const next = data?.pagingMetadata?.cursors?.next;
        if (!next) {
            return summaries;
        }
        cursor = next;
    }
    return summaries;
};
/**
 * The field types each of these forms would mount, so a picker can tell which forms the renderer can
 * actually draw (`areFieldTypesSupported`).
 *
 * A separate call because the picker's own `listForms` asks for `Fieldset.METADATA`, which the API
 * documents as excluding fields and steps. Lighter than `createLoadForms` for this purpose: no
 * translations and no locale dataset, which the picker has no use for.
 */
export const createGetFormFieldTypes = (httpClient) => async (formIds) => {
    const forms = await fetchForms(httpClient, formIds);
    const byId = {};
    Object.entries(forms).forEach(([id, form]) => {
        byId[id] = getRenderedFieldTypes(form);
    });
    return byId;
};
const toPlanLimit = (limit) => limit ? { limit: limit.limit ?? null, warnThreshold: limit.warnThreshold ?? null } : undefined;
/**
 * Reads the limits the site's premium plan places on its forms, and how many forms already count
 * against the form cap. This is the same call the forms service enforces a create against, so a host that
 * checks it first can never disagree with a rejected create.
 */
export const createGetFormRestrictions = (httpClient) => async () => {
    const { data } = await httpClient.request(getRestrictions({}));
    return {
        // Both an absent `formsLimit` and an explicit `null` mean the plan places no cap.
        limit: data?.restrictions?.formsLimit?.limit ?? null,
        totalFormCount: data?.totalFormCount ?? 0,
        fieldsLimit: toPlanLimit(data?.restrictions?.fieldsLimit),
        // Not the deprecated `conditionsLimit`, which carries the same value.
        rulesLimit: toPlanLimit(data?.restrictions?.rulesLimit),
        stepsLimit: toPlanLimit(data?.restrictions?.stepsLimit),
        features: data?.restrictions?.features ?? [],
    };
};
/**
 * `@wix/form-viewer` does not export its own submit, so this reproduces it over the same API.
 * No `status`: the service owns it.
 */
export const createSubmitForm = (httpClient, requestCaptcha) => async (formId, values) => {
    const captchaToken = await requestCaptcha();
    const { data } = await httpClient.request(createSubmission({ submission: { formId, submissions: values }, captchaToken }));
    return data;
};
//# sourceMappingURL=forms-api.js.map